// src/api/apiClient.js
import axios from 'axios';
import { getToken, saveToken, getRefreshToken, saveRefreshToken, clearAllTokens } from '../../APIServices/TokenService';
import { MessageType, showNotification } from '../../app_components/common/CommonStyle';
import { showModal } from '../../redux/modal/modalSlice';
import store from '../../redux/store';
const API_ENDPOINT = `${process.env.REACT_APP_APIURL_ENDPOINT}/api`;

const client = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
});

const authHeader = () => {
  const token = getToken();
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
    };
  }
  return {};
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  const response = await axios.post(`${API_ENDPOINT}/login/refresh`, {
    refreshToken,
  });

  if (response.data?.flag === true && response.data?.data) {
    const { token, refreshToken: newRefreshToken } = response.data.data;
    saveToken(token);
    if (newRefreshToken) saveRefreshToken(newRefreshToken);
    return token;
  }

  throw new Error('Refresh failed');
};

function logout() {
  clearAllTokens();
  window.localStorage.clear();
  window.location.href = '/';
}

async function get(path = '') {
  try {
    const response = await client({
      method: 'GET',
      url: path,
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
}

async function post(path = '', data = {}, optionalHeader = {}) {
  try {
    const response = await client({
      method: 'POST',
      url: path,
      data,
      headers: path === 'Login' ? {} : { ...authHeader(), ...optionalHeader },
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
}

async function postExternal(path = '', params = {}, optionalHeader = {}) {
  try {
    const response = await client({
      method: 'POST',
      url: path,
      params,
      headers: {
        'Content-Type': 'application/json',
        skipAuth: true,
        ...optionalHeader,
      },
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
}

async function postBody(path = '', data = {}, optionalHeader = {}) {
  try {
    const response = await client({
      method: 'POST',
      url: path,
      body: data,
      headers: path === 'Login' ? {} : { ...authHeader(), ...optionalHeader },
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
}

async function put(path = '', data = {}, optionalHeader = {}) {
  try {
    const response = await client({
      method: 'PUT',
      url: path,
      data,
      headers: { ...authHeader(), ...optionalHeader },
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
}

async function del(path = '', optionalHeader = {}) {
  try {
    const response = await client({
      method: 'DELETE',
      url: path,
      headers: { ...authHeader(), ...optionalHeader },
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
}

function handleResponse(response) {
  return response.data;
}

function handleError(error) {
  if (error.response) {
    const { status } = error.response;
    console.log(`Error Status: ${status}`);
    if (status === 401) {
      logout();
    } else if (status === 405) {
      console.log('Method Not Allowed (405) - No action taken.');
      return {};
    }
  }

  return Promise.reject(error);
}

client.interceptors.request.use((config) => {
  const requestConfig = config;
  const skipAuth = config?.headers?.skipAuth;

  if (!skipAuth) {
    const authHeaders = authHeader();
    requestConfig.headers = { ...config.headers, ...authHeaders };
  }

  // Remove custom flag so it's not sent to server
  delete requestConfig.headers.skipAuth;
  return requestConfig;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (!response || response.status !== 401 || config?.url === '/login/refresh') {
      if (response && response.status === 405) {
        console.log('Interceptor - Method Not Allowed (405) - No action taken.');
        return Promise.resolve({});
      }
      return Promise.reject(error);
    }

    if (config?.url === 'Login') {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        config.headers.Authorization = `Bearer ${token}`;
        return client(config);
      });
    }

    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      processQueue(null, newToken);
      config.headers.Authorization = `Bearer ${newToken}`;
      return client(config);
    } catch (refreshError) {
      processQueue(refreshError, null);
      logout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export { get, post, put, del, handleResponse, handleError, logout, postBody, postExternal };
